import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, HeartHandshake, Sunrise, School, Video, Library,
  FlaskConical, ShieldCheck, Sparkles, Users, Wrench, Home as HomeIcon,
  CalendarCheck, ClipboardList, MessagesSquare, TrendingUp, Quote, Star, Sprout,
} from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { CTASection } from "@/components/CTASection";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { WHY_US, PROCESS_STEPS, IMAGES } from "@/data/content";
import { useContent } from "@/context/ContentContext";

const ICONS = {
  HeartHandshake, Sunrise, School, Video, Library,
  FlaskConical, ShieldCheck, Sparkles, Users, Wrench, Home: HomeIcon,
  CalendarCheck, ClipboardList, MessagesSquare, TrendingUp,
};

const MARQUEE_ITEMS = [
  "Evidence-Informed Practice", "Trauma-Informed", "Neurodiversity-Affirming",
  "Family-Centered", "Collaborative", "Practical Strategies",
];

const BENTO_SPANS = [
  "md:col-span-7", "md:col-span-5", "md:col-span-4",
  "md:col-span-8", "md:col-span-12",
];

function Hero() {
  const { settings } = useContent();
  const btn = settings?.buttons || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-sage-200/50 blur-3xl dark:bg-forest-800/40" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[380px] w-[380px] rounded-full bg-terracotta-100/60 blur-3xl dark:bg-terracotta-500/10" />

      <div className="container-site relative grid items-center gap-14 pb-24 pt-16 sm:pt-24 lg:grid-cols-12 lg:gap-8 lg:pb-32">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="overline-label"
            data-testid="hero-overline"
          >
            Evol &amp; Troppus · Parent Coaching &amp; Family Support
          </motion.p>

          <h1 className="mt-6 font-heading text-4xl font-semibold leading-[1.06] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Helping caregivers</MaskedLine>
            <MaskedLine delay={0.3}>build confidence, connection,</MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="text-terracotta-500">and consistency.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg"
            data-testid="hero-subheadline"
          >
            Evol &amp; Troppus provides evidence-informed parent coaching, family
            support, school collaboration, and practical tools that help caregivers
            navigate everyday challenges with confidence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link to={btn.hero_primary_link || "/book"} data-testid="hero-book-button" className="btn-primary">
              {btn.hero_primary_label || "Book a Consultation"} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to={btn.hero_secondary_link || "/services"} data-testid="hero-learn-more-button" className="btn-secondary">
              {btn.hero_secondary_label || "Learn More"}
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.18em] text-muted-foreground"
          >
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-sage-600 dark:text-sage" /> Trauma-informed</span>
            <span className="flex items-center gap-2"><FlaskConical className="h-4 w-4 text-sage-600 dark:text-sage" /> Evidence-informed</span>
            <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-sage-600 dark:text-sage" /> Neurodiversity-affirming</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-4 -z-10 rounded-t-full rounded-b-3xl bg-sage-200/60 dark:bg-forest-800/60" />
            <div className="overflow-hidden rounded-t-full rounded-b-3xl shadow-lift dark:shadow-soft-dark">
              <motion.img
                src={IMAGES.hero}
                alt="A calm, light-filled family home interior"
                data-testid="hero-image"
                style={{ y: imgY, scale: 1.15 }}
                className="h-[420px] w-full object-cover sm:h-[520px]"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-6 -left-4 flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-soft sm:-left-10"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest/10 text-forest dark:bg-terracotta-500/15 dark:text-terracotta-400">
                <Sprout className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground">Growth through understanding</p>
                <p className="text-xs text-muted-foreground">Not just changing behavior</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  return (
    <div className="border-y border-border bg-forest py-5 dark:bg-forest-800" data-testid="values-marquee">
      <Marquee speed={32} gradient={false} pauseOnHover aria-label="Our commitments">
        {MARQUEE_ITEMS.map((item) => (
          <span key={item} className="mx-8 flex items-center gap-8 text-sm uppercase tracking-[0.24em] text-cream/80">
            {item}
            <Sparkles className="h-3.5 w-3.5 text-terracotta-400" aria-hidden="true" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function WhatWeDo() {
  const { services } = useContent();
  return (
    <section className="container-site py-24 sm:py-32" data-testid="what-we-do-section">
      <Reveal>
        <SectionHeader
          overline="What we do"
          title="Support for every part of family life"
          description="Practical coaching and tools for the moments that matter — mornings, mealtimes, school meetings, and everything in between."
        />
      </Reveal>
      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12">
        {services.map((s, i) => {
          const Icon = ICONS[s.icon] || HeartHandshake;
          return (
            <Reveal key={s.slug} delay={i * 0.06} className={BENTO_SPANS[i] || "md:col-span-6"}>
              <Link
                to={s.href || `/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="card-lift group flex h-full flex-col justify-between rounded-3xl border border-border bg-card p-8 shadow-soft"
              >
                <div>
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest/10 text-forest transition-colors duration-300 group-hover:bg-terracotta-500/15 group-hover:text-terracotta-500 dark:bg-sage/10 dark:text-sage dark:group-hover:text-terracotta-400">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-6 font-heading text-xl font-semibold text-foreground">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.short}</p>
                </div>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-forest transition-colors duration-300 group-hover:text-terracotta-500 dark:text-sage dark:group-hover:text-terracotta-400">
                  Explore <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section className="border-y border-border bg-card py-24 sm:py-32" data-testid="why-us-section">
      <div className="container-site grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <SectionHeader
                overline="Why Evol & Troppus"
                title="Change that respects the person"
                description="Growth happens through understanding people — not simply changing behavior. These commitments shape every plan we build."
              />
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative mt-10 hidden overflow-hidden rounded-3xl lg:block">
                <img
                  src={IMAGES.leaf}
                  alt="Close-up of a sage green leaf"
                  className="h-72 w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-forest/25" />
              </div>
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="grid gap-5 sm:grid-cols-2">
            {WHY_US.map((w, i) => {
              const Icon = ICONS[w.icon];
              return (
                <Reveal key={w.title} delay={i * 0.06}>
                  <div
                    data-testid={`why-card-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="card-lift h-full rounded-3xl border border-border bg-background p-7"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-terracotta-500/12 text-terracotta-500">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-heading text-lg font-semibold text-foreground">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="container-site py-24 sm:py-32" data-testid="how-it-works-section">
      <Reveal>
        <SectionHeader
          overline="How it works"
          title="A clear path from first call to lasting change"
          align="center"
        />
      </Reveal>
      <div className="relative mt-16">
        <div className="absolute left-1/2 top-8 hidden h-px w-[78%] -translate-x-1/2 bg-gradient-to-r from-transparent via-sage to-transparent lg:block" aria-hidden="true" />
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS_STEPS.map((step, i) => {
            const Icon = ICONS[step.icon];
            return (
              <Reveal key={step.title} delay={i * 0.12}>
                <div className="relative text-center" data-testid={`process-step-${i + 1}`}>
                  <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-border bg-card shadow-soft">
                    <Icon className="h-6 w-6 text-forest dark:text-sage" />
                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-terracotta-500 text-[11px] font-semibold text-cream">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="mt-6 font-heading text-lg font-semibold text-foreground">{step.title}</h3>
                  <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-muted-foreground">{step.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const { testimonials } = useContent();
  return (
    <section className="border-y border-border bg-card py-24 sm:py-32" data-testid="testimonials-section">
      <div className="container-site">
        <Reveal>
          <SectionHeader
            overline="Testimonials"
            title="Words from the people we serve"
            description="Stories from the families and caregivers we support."
          />
        </Reveal>
        {testimonials.length === 0 ? (
          <Reveal>
            <div
              data-testid="testimonials-coming-soon"
              className="mx-auto mt-14 max-w-xl rounded-3xl border border-dashed border-sage-400/60 bg-background p-12 text-center"
            >
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-forest/10 text-forest dark:bg-sage/10 dark:text-sage">
                <Quote className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-xl font-semibold text-foreground">Testimonials Coming Soon</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                We're gathering stories from the caregivers and families we support, and we'll
                share them here — with their permission — as they come in.
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {testimonials.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <figure
                data-testid={`testimonial-card-${i + 1}`}
                className="card-lift flex h-full flex-col rounded-3xl border border-border bg-background p-8 shadow-soft"
              >
                <Quote className="h-7 w-7 text-terracotta-500/60" aria-hidden="true" />
                <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-foreground/90 md:text-base">
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-7 border-t border-border pt-5">
                  <div className="flex gap-1" aria-label="5 out of 5 stars">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <Star key={s} className="h-3.5 w-3.5 fill-terracotta-500 text-terracotta-500" />
                    ))}
                  </div>
                  <p className="mt-3 text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </figcaption>
              </figure>
            </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function HomeFaq() {
  const { homeFaqs } = useContent();
  return (
    <section className="container-site py-24 sm:py-32" data-testid="home-faq-section">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <SectionHeader
              overline="FAQ"
              title="Questions, answered honestly"
              description="The things families and organizations ask us most. Something else on your mind?"
            />
            <Link to="/faq" data-testid="home-faq-view-all" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-forest transition-colors duration-300 hover:text-terracotta-500 dark:text-sage dark:hover:text-terracotta-400">
              View all questions <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="home-faq-accordion">
              {homeFaqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-border">
                  <AccordionTrigger
                    data-testid={`home-faq-trigger-${i + 1}`}
                    className="text-left font-heading text-base font-medium text-foreground hover:text-terracotta-500 hover:no-underline"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <EditorialMarquee />
      <WhatWeDo />
      <WhyUs />
      <HowItWorks />
      <Testimonials />
      <HomeFaq />
      <CTASection />
    </>
  );
}
